import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * The Inbox is a lens over multiple underlying primitives:
 *   - plansTable (pending_approval) → approval items
 *   - tasksTable (stage=awaiting_input) → review items
 *   - (future) task_events of type=question → question items
 *   - (future) task_events of type=failure → failure items
 *   - (future) task_suggestions → suggestion items
 *
 * Rather than invent a new top-level entity, each inbox item is keyed by
 * (source_type, source_id) and carries the agent's "key ask" alongside a
 * pointer to the full task thread. An item is "resolved" when the user
 * takes an action (approve/reject/answer/archive) — the underlying record
 * is mutated, and the inbox computes a new seen_at for history.
 *
 * This hook matches the shape of a future GET /api/inbox endpoint:
 *   { data: InboxItem[], has_more: boolean }
 *
 * Today it runs on an in-memory stub adapter so the UI can ship before
 * the backend is in place. Swap `STUB_ADAPTER` for `apiClient.listInbox()`
 * when the route lands. See foundation/_docs/inbox-backend.md.
 */

// ─── Types ──────────────────────────────────────────────────────────────

export type InboxItemKind =
  | 'approval'   // plan pending_approval
  | 'review'    // task in awaiting_input after agent completion
  | 'question'  // agent needs clarification
  | 'failure'   // agent errored & needs human intervention
  | 'suggestion'; // agent proactively surfaced an opportunity

export type InboxItemPriority = 'high' | 'med' | 'low';

export interface InboxAgent {
  id: string;
  name: string;
  initials: string;
  color?: 'primary' | 'info' | 'warning' | 'success' | 'error';
}

export interface InboxItem {
  object: 'inbox_item';
  id: string;                    // synthesized: `ibx_<sourceType>_<sourceId>`
  source_type: 'plan' | 'task' | 'task_event';
  source_id: string;             // ID of the underlying record
  task_id: string;               // owning task for thread context
  conversation_id: string;
  tenant_id: string;
  kind: InboxItemKind;
  priority: InboxItemPriority;
  title: string;
  summary: string;
  key_ask: string;               // the single sentence the agent is asking
  agent: InboxAgent;
  unread: boolean;
  received_at: string;           // ISO-8601
  seen_at: string | null;
  resolved_at: string | null;
  resolved_action: string | null; // 'approved' | 'rejected' | 'replied' | 'archived' | ...
  resolved_by: string | null;
  artifacts_count: number;
  metadata: Record<string, unknown>;
}

export interface InboxFilters {
  search: string;
  kinds: InboxItemKind[];
  show_resolved: boolean;
  grouping: 'stage' | 'agent' | 'priority' | 'none';
}

// ─── Stub adapter (remove when real API lands) ──────────────────────────

const NOW = Date.now();

const STUB_ITEMS: InboxItem[] = [
  {
    object: 'inbox_item',
    id: 'ibx_plan_pln_revenue_dataaccess',
    source_type: 'plan',
    source_id: 'pln_revenue_dataaccess',
    task_id: 'tsk_design_revenue_report',
    conversation_id: 'cnv_revenue_report',
    tenant_id: 'tnt_payengine',
    kind: 'approval',
    priority: 'high',
    title: 'Revenue report — approve data access before I run',
    summary: 'Needs read access to Stripe payments (last 90d) and the merchant directory.',
    key_ask: 'Approve access to 2 resources so I can run the Q4 revenue report?',
    agent: { id: 'agt_atlas', name: 'Atlas', initials: 'A', color: 'info' },
    unread: true,
    received_at: new Date(NOW - 1000 * 60 * 4).toISOString(),
    seen_at: null,
    resolved_at: null,
    resolved_action: null,
    resolved_by: null,
    artifacts_count: 2,
    metadata: { plan_version: 3, step_count: 5 },
  },
  {
    object: 'inbox_item',
    id: 'ibx_task_tsk_dispute_reply',
    source_type: 'task',
    source_id: 'tsk_dispute_reply',
    task_id: 'tsk_dispute_reply',
    conversation_id: 'cnv_dispute_auto_triage',
    tenant_id: 'tnt_payengine',
    kind: 'review',
    priority: 'med',
    title: 'Dispute 4855 — draft reply is ready for your review',
    summary: 'Revised rebuttal with signed delivery proof attached.',
    key_ask: 'Ready to send? I can queue it or revise further.',
    agent: { id: 'agt_atlas', name: 'Atlas', initials: 'A', color: 'info' },
    unread: true,
    received_at: new Date(NOW - 1000 * 60 * 18).toISOString(),
    seen_at: null,
    resolved_at: null,
    resolved_action: null,
    resolved_by: null,
    artifacts_count: 4,
    metadata: { draft_to: 'disputes@stripe.com', attachments: 3 },
  },
  {
    object: 'inbox_item',
    id: 'ibx_task_event_evt_refund_q1',
    source_type: 'task_event',
    source_id: 'evt_refund_question_1',
    task_id: 'tsk_refund_pipeline',
    conversation_id: 'cnv_refund_pipeline',
    tenant_id: 'tnt_payengine',
    kind: 'question',
    priority: 'med',
    title: 'Refund for order #SO-41892 — ambiguous policy match',
    summary: 'Customer ordered a digital product and used it. Policy allows partial refund.',
    key_ask: 'Offer 50% refund or 100% store credit?',
    agent: { id: 'agt_atlas', name: 'Atlas', initials: 'A', color: 'info' },
    unread: false,
    received_at: new Date(NOW - 1000 * 60 * 60 * 2).toISOString(),
    seen_at: new Date(NOW - 1000 * 60 * 60).toISOString(),
    resolved_at: null,
    resolved_action: null,
    resolved_by: null,
    artifacts_count: 1,
    metadata: {},
  },
  {
    object: 'inbox_item',
    id: 'ibx_task_event_evt_oauth_fail',
    source_type: 'task_event',
    source_id: 'evt_oauth_expired_qb',
    task_id: 'tsk_qb_reconcile',
    conversation_id: 'cnv_qb_reconcile',
    tenant_id: 'tnt_payengine',
    kind: 'failure',
    priority: 'high',
    title: 'QuickBooks reconciliation — OAuth token expired',
    summary: 'I tried refreshing twice. The connector needs re-authorization.',
    key_ask: 'Re-authorize QuickBooks so I can resume the reconciliation?',
    agent: { id: 'agt_atlas', name: 'Atlas', initials: 'A', color: 'info' },
    unread: false,
    received_at: new Date(NOW - 1000 * 60 * 60 * 4).toISOString(),
    seen_at: new Date(NOW - 1000 * 60 * 60 * 3).toISOString(),
    resolved_at: null,
    resolved_action: null,
    resolved_by: null,
    artifacts_count: 0,
    metadata: { error_code: 'OAUTH_EXPIRED', connector: 'quickbooks' },
  },
  {
    object: 'inbox_item',
    id: 'ibx_task_event_evt_suggest_1',
    source_type: 'task_event',
    source_id: 'evt_suggestion_slow_invoices',
    task_id: 'tsk_ar_scan',
    conversation_id: 'cnv_ar_ops',
    tenant_id: 'tnt_payengine',
    kind: 'suggestion',
    priority: 'low',
    title: '14 invoices over 45 days past due — want me to chase them?',
    summary: 'I can draft dunning emails with a 5% early-settlement offer.',
    key_ask: 'Kick off a dunning run? I\u2019ll draft each one for you to approve.',
    agent: { id: 'agt_atlas', name: 'Atlas', initials: 'A', color: 'info' },
    unread: false,
    received_at: new Date(NOW - 1000 * 60 * 60 * 8).toISOString(),
    seen_at: new Date(NOW - 1000 * 60 * 60 * 7).toISOString(),
    resolved_at: null,
    resolved_action: null,
    resolved_by: null,
    artifacts_count: 0,
    metadata: { affected_invoices: 14 },
  },
  {
    object: 'inbox_item',
    id: 'ibx_plan_pln_onboard_m41',
    source_type: 'plan',
    source_id: 'pln_onboard_m41',
    task_id: 'tsk_design_onboard_merchant',
    conversation_id: 'cnv_onboard_merchant',
    tenant_id: 'tnt_payengine',
    kind: 'approval',
    priority: 'med',
    title: 'Onboard merchant M-41 — approve the rollout plan',
    summary: '7 steps including KYC, Stripe account, and welcome email.',
    key_ask: 'Approve the full plan or pick specific steps?',
    agent: { id: 'agt_atlas', name: 'Atlas', initials: 'A', color: 'info' },
    unread: false,
    received_at: new Date(NOW - 1000 * 60 * 60 * 26).toISOString(),
    seen_at: new Date(NOW - 1000 * 60 * 60 * 25).toISOString(),
    resolved_at: new Date(NOW - 1000 * 60 * 60 * 24).toISOString(),
    resolved_action: 'approved',
    resolved_by: 'usr_jamie',
    artifacts_count: 3,
    metadata: { plan_version: 1, step_count: 7 },
  },
  {
    object: 'inbox_item',
    id: 'ibx_task_tsk_weekly_close',
    source_type: 'task',
    source_id: 'tsk_weekly_close',
    task_id: 'tsk_weekly_close',
    conversation_id: 'cnv_finance_ops',
    tenant_id: 'tnt_payengine',
    kind: 'review',
    priority: 'low',
    title: 'Weekly close — numbers look good, please sign off',
    summary: 'Revenue $184,220 · Refunds $2,145 · Net $182,075.',
    key_ask: 'Sign off and I\u2019ll file it in finance ops?',
    agent: { id: 'agt_atlas', name: 'Atlas', initials: 'A', color: 'info' },
    unread: false,
    received_at: new Date(NOW - 1000 * 60 * 60 * 48).toISOString(),
    seen_at: new Date(NOW - 1000 * 60 * 60 * 47).toISOString(),
    resolved_at: new Date(NOW - 1000 * 60 * 60 * 46).toISOString(),
    resolved_action: 'approved',
    resolved_by: 'usr_jamie',
    artifacts_count: 1,
    metadata: {},
  },
];

// ─── Hook ───────────────────────────────────────────────────────────────

export interface UseInboxOptions {
  enabled?: boolean;
  pollInterval?: number;
}

export function useInbox({ enabled = true, pollInterval = 15000 }: UseInboxOptions = {}) {
  const [items, setItems] = useState<InboxItem[]>(() => [...STUB_ITEMS]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [selectedId, setSelectedId] = useState<string | null>(() => STUB_ITEMS[0]?.id ?? null);

  const [filters, setFilters] = useState<InboxFilters>({
    search: '',
    kinds: [],
    show_resolved: true,
    grouping: 'stage',
  });

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fake "load" — would be apiClient.listInbox() in the real impl
  const reload = useCallback(() => {
    if (!enabled) return;
    setStatus('success');
    setError(undefined);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    reload();
  }, [enabled, reload]);

  useEffect(() => {
    if (!enabled || pollInterval <= 0) return;
    pollRef.current = setInterval(() => reload(), pollInterval);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [enabled, pollInterval, reload]);

  const selectItem = useCallback(
    (id: string | null) => {
      setSelectedId(id);
      // mark as seen
      if (id) {
        setItems((prev) =>
          prev.map((it) =>
            it.id === id && it.unread ? { ...it, unread: false, seen_at: new Date().toISOString() } : it,
          ),
        );
      }
    },
    [],
  );

  const resolveItem = useCallback(
    (id: string, action: string) => {
      // Optimistic: real impl calls POST /api/inbox/{id}/resolve with the action.
      // Server-side, that mutates the underlying plan/task/event.
      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? {
                ...it,
                resolved_at: new Date().toISOString(),
                resolved_action: action,
                resolved_by: 'usr_current',
                unread: false,
                seen_at: it.seen_at ?? new Date().toISOString(),
              }
            : it,
        ),
      );
    },
    [],
  );

  const archiveItem = useCallback((id: string) => resolveItem(id, 'archived'), [resolveItem]);

  // Filtered + sorted list for the UI
  const visibleItems = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return items
      .filter((it) => {
        if (!filters.show_resolved && it.resolved_at) return false;
        if (filters.kinds.length > 0 && !filters.kinds.includes(it.kind)) return false;
        if (q && !(it.title.toLowerCase().includes(q) || it.summary.toLowerCase().includes(q))) return false;
        return true;
      })
      .sort((a, b) => {
        // Unresolved first, then newest
        if (!!a.resolved_at !== !!b.resolved_at) return a.resolved_at ? 1 : -1;
        return b.received_at.localeCompare(a.received_at);
      });
  }, [items, filters]);

  const counts = useMemo(() => {
    const base: Record<InboxItemKind, number> = {
      approval: 0,
      review: 0,
      question: 0,
      failure: 0,
      suggestion: 0,
    };
    let unread = 0;
    let needsYou = 0;
    for (const it of items) {
      base[it.kind] += 1;
      if (it.unread) unread += 1;
      if (!it.resolved_at) needsYou += 1;
    }
    return { byKind: base, unread, needsYou, total: items.length };
  }, [items]);

  const selectedItem = useMemo(
    () => visibleItems.find((it) => it.id === selectedId) ?? null,
    [visibleItems, selectedId],
  );

  return {
    items: visibleItems,
    allItems: items,
    status,
    error,
    counts,
    selectedId,
    selectedItem,
    selectItem,
    resolveItem,
    archiveItem,
    filters,
    setFilters,
    reload,
  };
}
